import { pdfjs } from 'react-pdf'
import config from 'config/app'
import { PdfFormField } from 'types/document-generation.types'

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`

export const getTemplatePdfUrl = ({
  bucket = config?.aws?.assetsBucket,
  folder,
  fileName,
}: {
  bucket?: string
  folder: string
  fileName: string
}) => `https://${bucket}.s3.amazonaws.com/${folder}/${fileName}`

export const extractPdfFormFields = async (pdfUrl: string): Promise<PdfFormField[]> => {
  const pdfJsDoc = await pdfjs.getDocument(pdfUrl).promise
  const fields: PdfFormField[] = []
  const fieldNames = new Set<string>()

  for (let pageNum = 1; pageNum <= pdfJsDoc.numPages; pageNum++) {
    try {
      const page = await pdfJsDoc.getPage(pageNum)
      const annotations = await page.getAnnotations()

      annotations.forEach((annotation: any) => {
        if (annotation.subtype !== 'Widget' || !annotation.fieldName || fieldNames.has(annotation.fieldName)) {
          return
        }

        const fieldType = annotation.fieldType || 'Tx'

        if (fieldType === 'Tx') {
          fieldNames.add(annotation.fieldName)
          fields.push({
            name: annotation.fieldName,
            type: 'text',
            originalValue: annotation.fieldValue || annotation.defaultFieldValue || '',
          })
        } else if (fieldType === 'Btn' && annotation.checkBox) {
          fieldNames.add(annotation.fieldName)
          // An unchecked checkbox reports the value "Off" - treat it as empty
          const isChecked = !!annotation.fieldValue && annotation.fieldValue !== 'Off'
          fields.push({
            name: annotation.fieldName,
            type: 'checkbox',
            originalValue: isChecked ? String(annotation.fieldValue) : '',
          })
        }
      })
    } catch (err) {
      console.warn(`Error extracting fields from page ${pageNum}:`, err)
    }
  }

  return fields
}

const normalizeFieldName = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '')

// Empty template fields not filled automatically: fuzzy by `autoFilledKeys`, exact by `autoFilledFieldNames`
export const getManualPdfFields = (
  fields: PdfFormField[],
  autoFilledKeys: string[],
  autoFilledFieldNames: string[] = [],
) => {
  const normalizedKeys = autoFilledKeys.map(normalizeFieldName)

  return fields.filter(({ originalValue, name }) => {
    const normalizedName = normalizeFieldName(name)
    return (
      !originalValue?.trim() &&
      !autoFilledFieldNames.includes(name) &&
      !normalizedKeys.some((key) => normalizedName.includes(key))
    )
  })
}

export const getFilledPdfFields = (values: Record<string, string>) =>
  Object.fromEntries(Object.entries(values).filter(([, value]) => value?.trim()))
